import { ExtendedWithProductListDefinition } from "@cahaba/ProductListFieldDefinition";
import { SelectProductChildFieldDefinition } from "@cahaba/SelectProductFieldDefinition";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav from "@insite/content-library/Components/SkipNav";
import { productCarouselStyles } from "@insite/content-library/Widgets/Common/ProductCarousel";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography from "@insite/mobius/Typography";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

import ProductCarouselProduct from "@cahaba/Overrides/Components/ProductCarouselProduct";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    items = "items",
    maxNumberOfColumns = "maxNumberOfColumns",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showLearnMore = "showLearnMore",
}

const enum itemFields {
    title = "title",
    featuredProduct = "featuredProduct",
    backgroungImage = "backgroungImage",
}

interface OwnCarouselProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.items]: OwnItemProps[];
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showLearnMore]: boolean;
    };
}
interface OwnItemProps {
    fields: {
        [itemFields.title]: string;
        [itemFields.featuredProduct]: string;
        [itemFields.backgroungImage]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnCarouselProps) => {
    const products = getProductsForProductInfoList(state, ownProps.id);
    return {
        productCount: products?.length ?? 0,
        errorMessage: getErrorMessage(state, ownProps.id),
        products,
    };
};

const mapDispatchToProps = {
    loadProductInfoList,
};

type FeaturedProductCarouselProps = OwnCarouselProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

const FeaturedProductCarousel: React.FC<FeaturedProductCarouselProps> = ({
    id,
    theme,
    fields,
    productCount,
    errorMessage,
    loadProductInfoList,
    shellContext,
    products,
}) => {
    const productIds = fields.items.map(item => item.fields[itemFields.featuredProduct]);

    React.useEffect(() => {
        loadProductInfoList({
            id,
            getProductCollectionParameter: {
                productIds,
                expand: ["images"],
            },
        });
    }, [fields.items]);

    const afterCarousel = React.createRef<HTMLSpanElement>();

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const maxNumberOfColumns = fields.maxNumberOfColumns;
    const getSlidesToScroll = React.useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else {
            localSlidesToScroll = 4;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [maxNumberOfColumns, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !productCount || productCount === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            loop: productCount > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, productCount]);

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...productCarouselStyles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!productCount || productCount === 0) {
        return null;
    }

    productCarouselStyles.titleText = {
        variant: "h2",
        weight: 300,
        color: "secondary.main",
        size: 50,
        css: css`
            text-align: center;
            margin-bottom: 30px;
            padding-top: 30px;
            letter-spacing: 1px;
        `,
    };

    productCarouselStyles.mainContainer = {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    };

    productCarouselStyles.carouselSlideInner = {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 20px 10px;
            display: flex;
            flex-direction: column;
            & > div {
                background: #ffffff;
            }
        `,
    };

    productCarouselStyles.carouselProductStyles = {
        addToCartButtonGridItem: {
            css: css`
                display: flex;
                flex-direction: column;
                justify-content: end;
                width: calc(100% + 20px);
                max-width: calc(100% + 20px) !important;
                margin-left: -10px !important;
            `,
        },
        productAddToCartButtonStyles: {
            css: css`
                width: 100%;
                margin: 0 -1px -1px 0;
                font-size: 14px;
                text-transform: uppercase;
            `,
            typographyProps: {
                transform: "uppercase",
                size: 14,
                css: css`
                    letter-spacing: 1px;
                    font-size: 14px;
                `,
            },
        },
    };

    const showBrandBlocks = fields.showBrandName && (products?.some(o => !!o.brand) ?? false);

    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={productCarouselStyles.skipCarouselButton}
                destination={afterCarousel}
            />
            <Typography {...productCarouselStyles.titleText}>{fields.title}</Typography>
            <GridContainer
                {...productCarouselStyles.mainContainer}
                data-test-selector={`productCarousel_${"carouselType"}`}
            >
                <GridItem {...productCarouselStyles.prevArrowGridItem}>
                    {slidesToScroll < productCount && (
                        <Button
                            {...productCarouselStyles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                </GridItem>
                <GridItem {...productCarouselStyles.carouselGridItem}>
                    <StyledWrapper {...productCarouselStyles.carouselWrapper}>
                        <StyledWrapper {...productCarouselStyles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper
                                {...productCarouselStyles.carouselSlidesContainer}
                                data-test-selector="slides"
                            >
                                {products?.map(product => {
                                    return (
                                        <StyledWrapper
                                            {...productCarouselStyles.carouselSlide}
                                            key={product.id}
                                            style={{ width: `calc(100% / ${slidesToScroll})` }}
                                        >
                                            <StyledWrapper
                                                {...productCarouselStyles.carouselSlideInner}
                                                id={`product_${product.id}`}
                                                data-test-selector="productContainer"
                                            >
                                                <ProductCarouselProduct
                                                    carouselId={id}
                                                    product={product}
                                                    showImage={fields.showImage}
                                                    showBrand={showBrandBlocks}
                                                    showTitle={fields.showTitle}
                                                    showPartNumbers={fields.showPartNumbers}
                                                    showPrice={fields.showPrice}
                                                    showAddToCart={fields.showAddToCart}
                                                    showAddToList={fields.showAddToList}
                                                    extendedStyles={productCarouselStyles.carouselProductStyles}
                                                    showLearnMore={fields.showLearnMore}
                                                />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    );
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...productCarouselStyles.nextArrowGridItem}>
                    {slidesToScroll < productCount && (
                        <Button
                            {...productCarouselStyles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

export const featuredProductFieldDefinition: SelectProductChildFieldDefinition = {
    name: itemFields.featuredProduct,
    displayName: "Featured Product",
    editorTemplate: "SelectProductField",
    defaultValue: "",
    isRequired: true,
};

export const definition: WidgetDefinition<ExtendedWithProductListDefinition> = {
    group: "Common",
    icon: "Carousel",
    displayName: "Featured Product Carousel",
    fieldDefinitions: [
        {
            name: fields.title,
            editorTemplate: "TextField",
            fieldType: "Translatable",
            displayName: "Carousel Title",
            defaultValue: "",
        },
        {
            name: fields.showImage,
            displayName: "Image",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            isEnabled: () => false,
            fieldType: "General",
            sortOrder: 8,
        },
        {
            name: fields.showTitle,
            displayName: "Title",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            isEnabled: () => false,
            fieldType: "General",
            sortOrder: 9,
        },
        {
            name: fields.showBrandName,
            displayName: "Brand Name",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 10,
        },
        {
            name: fields.showPartNumbers,
            displayName: "Part Numbers",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 11,
        },
        {
            name: fields.showPrice,
            displayName: "Price",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 12,
        },
        {
            name: fields.showAddToCart,
            displayName: "Add to Cart",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 13,
        },
        {
            name: fields.showAddToList,
            displayName: "Add to List",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 14,
        },
        {
            name: fields.showLearnMore,
            displayName: "Learn More",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 15,
        },
        {
            name: fields.items,
            editorTemplate: "ProductListField",
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: itemFields.title,
                    displayName: "Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                featuredProductFieldDefinition,
                {
                    name: itemFields.backgroungImage,
                    displayName: "Backgroud Image",
                    editorTemplate: "ImagePickerField",
                    defaultValue: "",
                },
            ],
        },
        {
            name: fields.maxNumberOfColumns,
            editorTemplate: "IntegerField",
            fieldType: "General",
            displayName: "Max Number of Columns",
            defaultValue: 4,
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule<ExtendedWithProductListDefinition> = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(FeaturedProductCarousel))),
    definition,
};

// The `WidgetModule` MUST be the default export of the widget file.
export default widgetModule;
